import { DateTime } from 'luxon';
import { defaultClosingHourAtDate, defaultOpeningHourAtDate, defaultSpaceTimeConfig } from './spacetime/spacetimeConfig.js';
import { zeroPad } from './utils/utils.js';
const defaultTimezone = 'Europe/Oslo';
// Pre-computed sentinel DateTimes for evaluateDateTime empty-string cases.
// These represent "forever" bounds (start of century / end of century).
const SENTINEL_START = DateTime.fromISO('2000-01-01');
const SENTINEL_END = DateTime.fromISO('2100-01-01');
// The order of days is important because the index modulo 7 is the day number
const days = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sun',
    'mon',
    'tue',
    'wed',
    'thu',
    'fri',
    'sat',
    'søn',
    'man',
    'tir',
    'ons',
    'tor',
    'fre',
    'lør',
    'søndag',
    'mandag',
    'tirsdag',
    'onsdag',
    'torsdag',
    'fredag',
    'lørdag'
];
const periodConstants = {
    now: 0,
    idag: 0,
    max: 30,
    maks: 30
};
var TimeMonth;
(function (TimeMonth) {
    TimeMonth[TimeMonth["January"] = 1] = "January";
    TimeMonth[TimeMonth["February"] = 2] = "February";
    TimeMonth[TimeMonth["March"] = 3] = "March";
    TimeMonth[TimeMonth["April"] = 4] = "April";
    TimeMonth[TimeMonth["May"] = 5] = "May";
    TimeMonth[TimeMonth["June"] = 6] = "June";
    TimeMonth[TimeMonth["July"] = 7] = "July";
    TimeMonth[TimeMonth["August"] = 8] = "August";
    TimeMonth[TimeMonth["September"] = 9] = "September";
    TimeMonth[TimeMonth["October"] = 10] = "October";
    TimeMonth[TimeMonth["November"] = 11] = "November";
    TimeMonth[TimeMonth["December"] = 12] = "December";
})(TimeMonth || (TimeMonth = {}));
var TimeWeekDay;
(function (TimeWeekDay) {
    TimeWeekDay[TimeWeekDay["Sunday"] = 0] = "Sunday";
    TimeWeekDay[TimeWeekDay["Monday"] = 1] = "Monday";
    TimeWeekDay[TimeWeekDay["Tuesday"] = 2] = "Tuesday";
    TimeWeekDay[TimeWeekDay["Wednesday"] = 3] = "Wednesday";
    TimeWeekDay[TimeWeekDay["Thursday"] = 4] = "Thursday";
    TimeWeekDay[TimeWeekDay["Friday"] = 5] = "Friday";
    TimeWeekDay[TimeWeekDay["Saturday"] = 6] = "Saturday";
})(TimeWeekDay || (TimeWeekDay = {}));
const timeToDayStr = (time) => {
    return `${time.year}-${zeroPad(time.month)}-${zeroPad(time.day)}`;
};
const timeFromDateTime = (dt) => {
    return {
        year: dt.year,
        month: dt.month,
        day: dt.day,
        weekday: dt.weekday,
        week: dt.weekNumber,
        hour: dt.hour,
        minute: dt.minute
    };
};
const timeToDateTime = (time) => {
    return DateTime.fromObject({
        year: time.year,
        month: time.month,
        day: time.day,
        hour: time.hour,
        minute: time.minute
    });
};
const timeAllWeekDays = Array.from({ length: 7 }, (_, i) => (i + 1));
const isWithinDayFilter = (dayFilter, day) => {
    const dateParts = dayFilter.split('-');
    const fromInclDay = days.indexOf(dateParts[0].toLocaleLowerCase()) % 7;
    const toInclDay = dateParts.length > 1
        ? days.indexOf(dateParts[1].toLocaleLowerCase()) % 7
        : fromInclDay;
    const currDay = day.weekday % 7;
    const fromBiggest = currDay >= fromInclDay || currDay <= toInclDay;
    const toBiggest = currDay >= fromInclDay && currDay <= toInclDay;
    return fromInclDay > toInclDay ? fromBiggest : toBiggest;
};
const isWithinHourFilter = (hourFilter, day) => {
    const timeParts = hourFilter.split('-');
    const fromInclTime = parseInt(timeParts[0].substring(0, 2));
    const toInclTime = timeParts.length > 1 ? parseInt(timeParts[1].substring(0, 2)) : fromInclTime;
    return fromInclTime > toInclTime
        ? day.hour >= fromInclTime || day.hour <= toInclTime
        : day.hour >= fromInclTime && day.hour <= toInclTime;
};
/**
 * An hour-relative period such as '2h', or undefined when `str` is not one.
 *
 * Separate from evaluatePeriod because hours are not convertible to whole days:
 * evaluateDateTime consumes evaluatePeriod's result as days and then snaps to a
 * day boundary. See the call site.
 */
const evaluateHourPeriod = (str) => {
    const match = /^(\d+)\s*h$/i.exec(str.trim());
    if (!match)
        return undefined;
    return parseInt(match[1], 10);
};
/**
 * Parse and convert periods like 5d and now to number of days
 *
 * @param {string} str Text representation of the period
 * @return {number} Number of days in that period or undefined if input is not a period
 */
const evaluatePeriod = (str) => {
    // Check if hardcoded periods
    if (!isNaN(periodConstants[str])) {
        return periodConstants[str];
    }
    // Check if type days or months or weeks
    const unit = str.slice(-1).toLocaleLowerCase();
    const value = str.slice(0, -1);
    if (!['d', 'm', 'w'].includes(unit)) {
        // 'h' is deliberately absent here: hours are an exact offset and are
        // handled in evaluateDateTime before this point, because this function's
        // result is consumed as a number of DAYS.
        throw Error('Kjenner ikke igjen periode enheten "' +
            unit +
            '" fra ' +
            str +
            '. Gyldige enheter: d (dager), w (uker), m (måneder), h (timer). ' +
            'Et tall alene er ikke gyldig - skriv f.eks. "2d" eller "2h".');
    }
    const intValue = parseInt(value);
    if (isNaN(intValue))
        throw Error('Forventet et tall etter "' + unit + '"');
    switch (unit) {
        case 'd':
            return intValue;
        case 'm':
            return intValue * 30;
        case 'w':
            return intValue * 7;
    }
    throw new Error('Kjenner ikke igjen: ' + str);
};
/**
 * Format: 2.mon where 2. is optional
 * Parse and evaluate number of days from a day to a previous weekday.
 * First monday before date or 2. wednesday before
 * @param {string} str
 * @param {DateTime} relativeTo which date we are looking for past weekdays
 * @return {DateTime} The date relative to the input
 */
const evaluateRelativeToWeekday = (str, relativeTo) => {
    const parts = str.split('.');
    const order = parts.length === 2 ? parseInt(parts[0]) : 1;
    const day = parts.length === 2 ? parts[1] : str;
    if (!days.includes(day)) {
        throw new Error('Kjenner ikke igjen ukedagen: ' + day);
    }
    const dayNumber = days.indexOf(day) % 7;
    if (isNaN(order)) {
        throw new Error('Kjenner ikke igjen tallet: ' + order);
    }
    let finalDate = relativeTo;
    for (let i = 0; i < order * 7; i++) {
        if (finalDate.weekday % 7 === dayNumber && i >= (order - 1) * 7) {
            return finalDate;
        }
        finalDate = finalDate.minus({ day: 1 });
    }
    return finalDate;
};
const evaluateTime = (str, fallbackHour) => {
    const parts = str.split('@');
    if (parts.length !== 2)
        return [fallbackHour, str];
    const hour = parseInt(parts[1], 10);
    if (hour < 0 || hour > 23 || isNaN(hour)) {
        throw new Error('Ugyldig tidsangivelse: ' + parts[1]);
    }
    return [hour, parts[0]];
};
const isValidDateTime = (str, relativeTo) => {
    try {
        evaluateDateTime(str, relativeTo, defaultSpaceTimeConfig());
    }
    catch {
        return false;
    }
    return true;
};
const evaluateDateTime = (rawStr, relativeTo, stConfig, start = true, hourCache) => {
    // Nothing means forever - which is translated to start or end of this century
    if (!rawStr || rawStr.trim().length < 1) {
        return start ? SENTINEL_START : SENTINEL_END;
    }
    const trimmed = rawStr.trim();
    // Check if str is an ISO date
    const luxonTime = DateTime.fromISO(trimmed, { zone: defaultTimezone });
    if (luxonTime.isValid) {
        if (trimmed.includes('T')) {
            // The ISO date also includes time - so we go with that
            return luxonTime;
        }
        else {
            // If no hour component exist in the string then we use the fallback hours
            // And adjust minutes and secconds according to if it is start or end of a period
            const adjustedTime = start
                ? luxonTime.startOf('day')
                : luxonTime.endOf('day');
            const fallbackHour = start
                ? defaultOpeningHourAtDate(adjustedTime, stConfig, hourCache)
                : defaultClosingHourAtDate(adjustedTime, stConfig, hourCache);
            return adjustedTime.set({ hour: fallbackHour });
        }
    }
    // If we hare here the str is of the custom format of 'man@08' or '5d' - both are
    // description of something relative to the relativeTo date. Admins type
    // these by hand, so 'Ons' and ' 5D ' read the same as 'ons' and '5d' (D9).
    const str = trimmed.toLocaleLowerCase();
    // Strip any hour components from the str.
    const [, strWithoutTime] = evaluateTime(str, 0);
    // Hour-relative periods ('2h') are an EXACT offset from relativeTo and return
    // early, before the day-based path below.
    //
    // They cannot go through evaluatePeriod: that returns a number of days, and
    // the code below then snaps to start/end of day and overwrites the hour with
    // the site's default opening/closing hour — which would discard the offset
    // entirely. Hours are the only way to express a window finer than a day, e.g.
    // "sales close 2 hours before the slot starts".
    const hoursBefore = evaluateHourPeriod(strWithoutTime);
    if (hoursBefore !== undefined) {
        if (strWithoutTime !== str) {
            // '2h@18' asks for both a relative offset and an absolute hour. Rejecting
            // is better than silently honouring one — a swallowed surprise here shows
            // up as a slot that is mysteriously never available.
            throw Error('Kan ikke kombinere timer-periode med klokkeslett: "' + str + '"');
        }
        return relativeTo.minus({ hours: hoursBefore });
    }
    // Check if format is 2.man or (i.e relative to a weekday)
    const restParts = strWithoutTime.split('.');
    const isWeekdayRelative = restParts.length === 2 || days.includes(strWithoutTime);
    // Calculate final date.
    let finalDateTime = isWeekdayRelative
        ? evaluateRelativeToWeekday(strWithoutTime, relativeTo)
        : relativeTo.minus({ days: evaluatePeriod(strWithoutTime) });
    // Now that we know the date - evaluate the hour based on defaults for that day or contents of str.
    const fallbackHour = start
        ? defaultOpeningHourAtDate(finalDateTime, stConfig, hourCache)
        : defaultClosingHourAtDate(finalDateTime, stConfig, hourCache);
    const [hour] = evaluateTime(str, fallbackHour);
    // Adjust the date to either the last second of first second of that hour.
    if (start) {
        finalDateTime = finalDateTime.startOf('day');
    }
    else {
        finalDateTime = finalDateTime.endOf('day');
    }
    finalDateTime = finalDateTime.set({ hour });
    return finalDateTime;
};
const timeSiteNow = (timezone) => {
    const dt = DateTime.now().setZone(timezone ?? defaultTimezone);
    return dt.isValid ? dt : DateTime.now();
};
const timeSiteParse = (date, timezone) => {
    return DateTime.fromISO(date, { zone: timezone ?? defaultTimezone });
};
const timeLocalNow = () => {
    return DateTime.now();
};
const timeLocalParse = (date) => {
    return DateTime.fromISO(date);
};
const nowAtSiteAsStr = () => {
    return timeSiteNow().toISO() ?? DateTime.now().toISO();
};
/**
 * Get current time in a specific timezone
 */
const timeInZone = (timezone) => {
    const dt = DateTime.now().setZone(timezone);
    return dt.isValid ? dt : DateTime.now();
};
/**
 * Get start of day in a specific timezone
 */
const startOfDayInZone = (dt, timezone) => {
    return dt.setZone(timezone).startOf('day');
};
/**
 * Get end of day in a specific timezone
 */
const endOfDayInZone = (dt, timezone) => {
    return dt.setZone(timezone).endOf('day');
};
/**
 * Parse ISO date string in a specific timezone
 */
const parseInZone = (date, timezone) => {
    return DateTime.fromISO(date, { zone: timezone });
};
/**
 * Convert DateTime to UTC ISO string
 */
const toUTCISO = (dt) => {
    return dt.toUTC().toISO() || '';
};
export { evaluateHourPeriod, evaluatePeriod, evaluateTime, evaluateRelativeToWeekday, evaluateDateTime, isWithinDayFilter, isWithinHourFilter, isValidDateTime, timeSiteParse, timeSiteNow, timeLocalNow, timeLocalParse, nowAtSiteAsStr, timeToDayStr, timeAllWeekDays, timeFromDateTime, timeToDateTime, timeInZone, startOfDayInZone, endOfDayInZone, parseInZone, toUTCISO };
//# sourceMappingURL=time.js.map